import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { InlineFormError } from "@/components/ui/inline-form-error";
import { Key, Eye, EyeOff, Save, CheckCircle2, AlertCircle, Zap, Building2, RefreshCw, Palette, Check, RotateCcw, Plus, Trash2, ExternalLink, Brain, Layout, Type, Image, Layers, Settings as SettingsIcon, CreditCard, CalendarDays, Receipt } from "lucide-react";
import { PageShell, PageStat } from "@/components/page-shell";
import { SiOpenai, SiGoogle } from "react-icons/si";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { planDisplayFeatures } from "@/lib/plan-features";
import { useToast } from "@/hooks/use-toast";
import { useAuth, useFeature } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import { useTheme } from "@/components/theme-provider";
import {
  PLATFORM_THEMES,
  getPlatformThemeById,
  applyPlatformTheme,
  hslToHex,
  hexToHsl,
  type ThemeColors,
} from "@/lib/platform-themes";

export interface LandingPageConfig {
  heroTitle: string;
  heroSubtitle: string;
  heroBadge: string;
  headerStyle: "default" | "centered" | "minimal";
  bannerGradientFrom: string;
  bannerGradientTo: string;
  bannerOverlayOpacity: number;
  fontFamily: string;
  headingSize: "small" | "medium" | "large";
  bodySize: "small" | "medium" | "large";
  showFeatures: boolean;
  featuresTitle: string;
  featuresSubtitle: string;
  showHowItWorks: boolean;
  showStats: boolean;
  showChannels: boolean;
  channelsTitle: string;
  channelsSubtitle: string;
  showIntegrations: boolean;
  integrationsTitle: string;
  integrationsSubtitle: string;
  showLiveAgent: boolean;
  liveAgentTitle: string;
  liveAgentSubtitle: string;
  showTrust: boolean;
  trustTitle: string;
  showCta: boolean;
  ctaTitle: string;
  ctaSubtitle: string;
  footerText: string;
}

export const DEFAULT_LANDING_CONFIG: LandingPageConfig = {
  heroTitle: "",
  heroSubtitle: "",
  heroBadge: "",
  headerStyle: "default",
  bannerGradientFrom: "",
  bannerGradientTo: "",
  bannerOverlayOpacity: 30,
  fontFamily: "Inter",
  headingSize: "large",
  bodySize: "medium",
  showFeatures: true,
  featuresTitle: "Intelligent Assistance at Your Fingertips",
  featuresSubtitle: "Our AI assistants are trained on our knowledge base to provide accurate, relevant answers to your questions.",
  showHowItWorks: true,
  showStats: true,
  showChannels: true,
  channelsTitle: "Reach Your Audience Anywhere",
  channelsSubtitle: "Deploy the same intelligent assistant across web, mobile, SMS, email, and any system that speaks REST.",
  showIntegrations: true,
  integrationsTitle: "Plugs Into Your Stack",
  integrationsSubtitle: "Pre-built connectors hand the AI the tools it needs — HRMS, ITSM, SIS, LMS, identity, and more.",
  showLiveAgent: true,
  liveAgentTitle: "Human Backup, One Click Away",
  liveAgentSubtitle: "When an answer needs a person, visitors are routed FIFO to a live operator with the full chat transcript pre-loaded.",
  showTrust: true,
  trustTitle: "Built for Enterprise & Higher-Ed Compliance",
  showCta: true,
  ctaTitle: "Ready to Get Started?",
  ctaSubtitle: "Choose one of our AI assistants above and start getting instant, intelligent answers to your questions.",
  footerText: "",
};

const FONT_OPTIONS = [
  { value: "Inter", label: "Inter (Default)" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Lato", label: "Lato" },
  { value: "Poppins", label: "Poppins" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Merriweather", label: "Merriweather" },
  { value: "Source Sans 3", label: "Source Sans 3" },
  { value: "Nunito", label: "Nunito" },
];

function ColorPicker({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId: string }) {
  const hexVal = hslToHex(value);
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={hexVal}
        onChange={(e) => onChange(hexToHsl(e.target.value))}
        className="w-8 h-8 rounded cursor-pointer border border-border flex-shrink-0"
        aria-label={`${label} colour picker`}
        data-testid={testId}
      />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{label}</p>
        <p className="text-[10px] text-muted-foreground">{hexVal}</p>
      </div>
    </div>
  );
}

const BRANDING_UPGRADE_MESSAGE =
  "Custom branding is not included in your current plan. Please upgrade your plan to customize the landing page.";

function brandingFriendlyError(err: any): string {
  const msg = String(err?.message ?? err ?? "");
  return msg.includes("plan_not_entitled") || msg.includes("PLAN_FEATURE_REQUIRED")
    ? BRANDING_UPGRADE_MESSAGE
    : msg;
}

function BrandingUpgradeNotice() {
  return (
    <Card data-testid="card-branding-upgrade">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Layout className="w-5 h-5" />
          Not included in your plan
        </CardTitle>
        <CardDescription data-testid="text-branding-upgrade">{BRANDING_UPGRADE_MESSAGE}</CardDescription>
      </CardHeader>
    </Card>
  );
}

function LandingPageTab({ organization }: { organization: any }) {
  const { toast } = useToast();
  let parsedConfig: Partial<LandingPageConfig> = {};
  try {
    if (organization?.landingPageConfig) {
      parsedConfig = typeof organization.landingPageConfig === "string"
        ? JSON.parse(organization.landingPageConfig)
        : organization.landingPageConfig;
    }
  } catch {
    parsedConfig = {};
  }
  const existing: LandingPageConfig = { ...DEFAULT_LANDING_CONFIG, ...parsedConfig };

  const [config, setConfig] = useState<LandingPageConfig>(existing);

  const update = (key: keyof LandingPageConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", "/api/organization", {
        landingPageConfig: JSON.stringify(config),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Landing page updated", description: "Your changes are now live on the landing page." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: brandingFriendlyError(err), variant: "destructive" });
    },
  });

  const brandColor = organization?.brandColor || "#0c3547";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layout className="w-5 h-5" />
            Header & Banner
          </CardTitle>
          <CardDescription>Customize the hero section of your landing page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hero-title">Hero Title</Label>
            <Input
              id="hero-title"
              placeholder={`Welcome to ${organization?.name || "Your Organization"}`}
              value={config.heroTitle}
              onChange={(e) => update("heroTitle", e.target.value)}
              data-testid="input-hero-title"
            />
            <p className="text-xs text-muted-foreground">Leave empty to use the default: "Welcome to [Organization Name]"</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hero-subtitle">Hero Subtitle</Label>
            <Textarea
              id="hero-subtitle"
              placeholder="Explore our AI-powered assistants, designed to help you find answers, get support, and accomplish more — instantly."
              value={config.heroSubtitle}
              onChange={(e) => update("heroSubtitle", e.target.value)}
              rows={3}
              data-testid="input-hero-subtitle"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hero-badge">Hero Badge (small pill above title)</Label>
            <Input
              id="hero-badge"
              placeholder="e.g. AI-Powered Concierge"
              value={config.heroBadge}
              onChange={(e) => update("heroBadge", e.target.value)}
              data-testid="input-hero-badge"
            />
            <p className="text-xs text-muted-foreground">Optional. Leave empty to hide.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="select-header-style">Header Layout</Label>
              <Select value={config.headerStyle} onValueChange={(v) => update("headerStyle", v)}>
                <SelectTrigger id="select-header-style" data-testid="select-header-style">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (Logo Left)</SelectItem>
                  <SelectItem value="centered">Centered</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="range-overlay-opacity">Banner Overlay Opacity</Label>
              <div className="flex items-center gap-3">
                <input
                  id="range-overlay-opacity"
                  type="range"
                  min="0"
                  max="100"
                  value={config.bannerOverlayOpacity}
                  onChange={(e) => update("bannerOverlayOpacity", parseInt(e.target.value))}
                  className="flex-1"
                  data-testid="range-overlay-opacity"
                />
                <span className="text-sm font-mono w-10 text-right">{config.bannerOverlayOpacity}%</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="input-gradient-from">Banner Gradient Start</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={config.bannerGradientFrom || brandColor}
                  onChange={(e) => update("bannerGradientFrom", e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-border"
                  aria-label="Banner gradient start colour picker"
                  data-testid="color-gradient-from"
                />
                <Input
                  id="input-gradient-from"
                  value={config.bannerGradientFrom}
                  onChange={(e) => update("bannerGradientFrom", e.target.value)}
                  placeholder="Auto from brand color"
                  data-testid="input-gradient-from"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="input-gradient-to">Banner Gradient End</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={config.bannerGradientTo || "#ffffff"}
                  onChange={(e) => update("bannerGradientTo", e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-border"
                  aria-label="Banner gradient end colour picker"
                  data-testid="color-gradient-to"
                />
                <Input
                  id="input-gradient-to"
                  value={config.bannerGradientTo}
                  onChange={(e) => update("bannerGradientTo", e.target.value)}
                  placeholder="Auto (white)"
                  data-testid="input-gradient-to"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Type className="w-5 h-5" />
            Typography
          </CardTitle>
          <CardDescription>Choose fonts and sizes for your landing page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="select-font-family">Font Family</Label>
              <Select value={config.fontFamily} onValueChange={(v) => update("fontFamily", v)}>
                <SelectTrigger id="select-font-family" data-testid="select-font-family">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="select-heading-size">Heading Size</Label>
              <Select value={config.headingSize} onValueChange={(v) => update("headingSize", v)}>
                <SelectTrigger id="select-heading-size" data-testid="select-heading-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large (Default)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="select-body-size">Body Text Size</Label>
              <Select value={config.bodySize} onValueChange={(v) => update("bodySize", v)}>
                <SelectTrigger id="select-body-size" data-testid="select-body-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium (Default)</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-4 rounded-lg border bg-muted/30">
            <p className="text-xs text-muted-foreground mb-2">Preview</p>
            <div style={{ fontFamily: `'${config.fontFamily}', sans-serif` }}>
              <p className={`font-bold mb-1 ${config.headingSize === "small" ? "text-xl" : config.headingSize === "medium" ? "text-2xl" : "text-3xl"}`}>
                Sample Heading Text
              </p>
              <p className={`text-muted-foreground ${config.bodySize === "small" ? "text-xs" : config.bodySize === "medium" ? "text-sm" : "text-base"}`}>
                This is how body text will appear on your landing page with the selected font and size.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Content Sections
          </CardTitle>
          <CardDescription>Show or hide sections and customize their content</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div>
              <p className="text-sm font-medium">Statistics Bar</p>
              <p className="text-xs text-muted-foreground">Show bot count, availability, response time stats</p>
            </div>
            <Switch
              checked={config.showStats}
              onCheckedChange={(v) => update("showStats", v)}
              aria-label="Statistics Bar"
              data-testid="switch-show-stats"
            />
          </div>

          <div className="space-y-3 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Features Section</p>
                <p className="text-xs text-muted-foreground">"Why Choose Us" feature cards</p>
              </div>
              <Switch
                checked={config.showFeatures}
                onCheckedChange={(v) => update("showFeatures", v)}
                aria-label="Features Section"
                data-testid="switch-show-features"
              />
            </div>
            {config.showFeatures && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-features-title">Section Title</Label>
                  <Input
                    id="input-features-title"
                    value={config.featuresTitle}
                    onChange={(e) => update("featuresTitle", e.target.value)}
                    placeholder="Intelligent Assistance at Your Fingertips"
                    data-testid="input-features-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-features-subtitle">Section Subtitle</Label>
                  <Input
                    id="input-features-subtitle"
                    value={config.featuresSubtitle}
                    onChange={(e) => update("featuresSubtitle", e.target.value)}
                    placeholder="Our AI assistants are trained on our knowledge base..."
                    data-testid="input-features-subtitle"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div>
              <p className="text-sm font-medium">How It Works Section</p>
              <p className="text-xs text-muted-foreground">3-step guide for using AI assistants</p>
            </div>
            <Switch
              checked={config.showHowItWorks}
              onCheckedChange={(v) => update("showHowItWorks", v)}
              aria-label="How It Works Section"
              data-testid="switch-show-how-it-works"
            />
          </div>

          <div className="space-y-3 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Channels Section</p>
                <p className="text-xs text-muted-foreground">Showcase Web / SMS / Email / Mobile / API delivery</p>
              </div>
              <Switch
                checked={config.showChannels}
                onCheckedChange={(v) => update("showChannels", v)}
                aria-label="Channels Section"
                data-testid="switch-show-channels"
              />
            </div>
            {config.showChannels && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-channels-title">Section Title</Label>
                  <Input
                    id="input-channels-title"
                    value={config.channelsTitle}
                    onChange={(e) => update("channelsTitle", e.target.value)}
                    placeholder="Reach Your Audience Anywhere"
                    data-testid="input-channels-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-channels-subtitle">Section Subtitle</Label>
                  <Input
                    id="input-channels-subtitle"
                    value={config.channelsSubtitle}
                    onChange={(e) => update("channelsSubtitle", e.target.value)}
                    placeholder="Deploy across web, mobile, SMS, email..."
                    data-testid="input-channels-subtitle"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Integrations Section</p>
                <p className="text-xs text-muted-foreground">Show pre-built connectors (HRMS, ITSM, SIS, LMS, identity)</p>
              </div>
              <Switch
                checked={config.showIntegrations}
                onCheckedChange={(v) => update("showIntegrations", v)}
                aria-label="Integrations Section"
                data-testid="switch-show-integrations"
              />
            </div>
            {config.showIntegrations && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-integrations-title">Section Title</Label>
                  <Input
                    id="input-integrations-title"
                    value={config.integrationsTitle}
                    onChange={(e) => update("integrationsTitle", e.target.value)}
                    placeholder="Plugs Into Your Stack"
                    data-testid="input-integrations-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-integrations-subtitle">Section Subtitle</Label>
                  <Input
                    id="input-integrations-subtitle"
                    value={config.integrationsSubtitle}
                    onChange={(e) => update("integrationsSubtitle", e.target.value)}
                    placeholder="Pre-built connectors hand the AI the tools it needs..."
                    data-testid="input-integrations-subtitle"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Live Agent Handoff Section</p>
                <p className="text-xs text-muted-foreground">Highlight the human-takeover capability</p>
              </div>
              <Switch
                checked={config.showLiveAgent}
                onCheckedChange={(v) => update("showLiveAgent", v)}
                aria-label="Live Agent Handoff Section"
                data-testid="switch-show-live-agent"
              />
            </div>
            {config.showLiveAgent && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-live-agent-title">Section Title</Label>
                  <Input
                    id="input-live-agent-title"
                    value={config.liveAgentTitle}
                    onChange={(e) => update("liveAgentTitle", e.target.value)}
                    placeholder="Human Backup, One Click Away"
                    data-testid="input-live-agent-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-live-agent-subtitle">Section Subtitle</Label>
                  <Input
                    id="input-live-agent-subtitle"
                    value={config.liveAgentSubtitle}
                    onChange={(e) => update("liveAgentSubtitle", e.target.value)}
                    placeholder="When an answer needs a person, visitors are routed FIFO..."
                    data-testid="input-live-agent-subtitle"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Trust & Compliance Section</p>
                <p className="text-xs text-muted-foreground">Display security/compliance badges (SSO, encryption, FERPA, etc.)</p>
              </div>
              <Switch
                checked={config.showTrust}
                onCheckedChange={(v) => update("showTrust", v)}
                aria-label="Trust & Compliance Section"
                data-testid="switch-show-trust"
              />
            </div>
            {config.showTrust && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-trust-title">Section Title</Label>
                  <Input
                    id="input-trust-title"
                    value={config.trustTitle}
                    onChange={(e) => update("trustTitle", e.target.value)}
                    placeholder="Built for Enterprise & Higher-Ed Compliance"
                    data-testid="input-trust-title"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Call to Action Section</p>
                <p className="text-xs text-muted-foreground">Bottom CTA banner</p>
              </div>
              <Switch
                checked={config.showCta}
                onCheckedChange={(v) => update("showCta", v)}
                aria-label="Call to Action Section"
                data-testid="switch-show-cta"
              />
            </div>
            {config.showCta && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-cta-title">CTA Title</Label>
                  <Input
                    id="input-cta-title"
                    value={config.ctaTitle}
                    onChange={(e) => update("ctaTitle", e.target.value)}
                    placeholder="Ready to Get Started?"
                    data-testid="input-cta-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" htmlFor="input-cta-subtitle">CTA Subtitle</Label>
                  <Input
                    id="input-cta-subtitle"
                    value={config.ctaSubtitle}
                    onChange={(e) => update("ctaSubtitle", e.target.value)}
                    placeholder="Choose one of our AI assistants..."
                    data-testid="input-cta-subtitle"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="input-footer-text">Footer Text</Label>
            <Input
              id="input-footer-text"
              value={config.footerText}
              onChange={(e) => update("footerText", e.target.value)}
              placeholder={`© ${new Date().getFullYear()} ${organization?.name || "Organization"}. All rights reserved.`}
              data-testid="input-footer-text"
            />
            <p className="text-xs text-muted-foreground">Leave empty to use the default copyright text</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <a
          href={`/org/${organization?.slug}`}
          target="_blank"
          className="text-sm text-primary underline flex items-center gap-1"
          data-testid="link-preview-landing"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Preview Landing Page
        </a>
        <InlineFormError error={saveMutation.isError ? saveMutation.error : null} data-testid="error-save-landing" />
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          data-testid="button-save-landing"
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? "Saving..." : "Save Landing Page"}
        </Button>
      </div>
    </div>
  );
}

interface VoiceProfile {
  enabled: boolean;
  tone: string;
  preferredTerms: string;
  prohibitedTerms: string;
  readingLevel: string;
  inclusivityStandard: string;
}

interface InclusiveEvent {
  id: string;
  chatbotId: string;
  chatbotName: string;
  conversationId: string | null;
  terms: string;
  rewritten: boolean;
  remainingTerms: string | null;
  createdAt: string;
}

interface InclusiveEventsResponse {
  total: number;
  rewritten: number;
  unresolved: number;
  lastSeen: string | null;
  recent: InclusiveEvent[];
}

function InclusiveEventsCard() {
  const { data, isLoading } = useQuery<InclusiveEventsResponse>({
    queryKey: ["/api/organization/inclusive-events"],
  });

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Card data-testid="card-inclusive-events">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Inclusive-language catches
        </CardTitle>
        <CardDescription>
          Every time a chatbot reply contained a prohibited term, the guard logs it here and attempts
          an automatic inclusive rewrite. Use this to audit how often non-inclusive language is caught
          and to tune your prohibited-terms list above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading || !data ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="text-inclusive-loading">
            Loading inclusive-language activity…
          </p>
        ) : data.total === 0 ? (
          <div className="py-8 text-center" data-testid="text-inclusive-empty">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No non-inclusive language has been caught yet. Replies are staying within your standards.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3" data-testid="stat-inclusive-total">
                <div className="text-2xl font-semibold">{data.total}</div>
                <div className="text-xs text-muted-foreground">Total caught</div>
              </div>
              <div className="rounded-lg border p-3" data-testid="stat-inclusive-rewritten">
                <div className="text-2xl font-semibold text-emerald-600 dark:text-emerald-500">{data.rewritten}</div>
                <div className="text-xs text-muted-foreground">Auto-corrected</div>
              </div>
              <div className="rounded-lg border p-3" data-testid="stat-inclusive-unresolved">
                <div className="text-2xl font-semibold text-amber-600 dark:text-amber-500">{data.unresolved}</div>
                <div className="text-xs text-muted-foreground">Not corrected</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Recent catches</Label>
              <div className="divide-y rounded-lg border">
                {data.recent.map((ev) => (
                  <div
                    key={ev.id}
                    className="flex items-start justify-between gap-3 p-3"
                    data-testid={`row-inclusive-event-${ev.id}`}
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {ev.terms.split(",").map((t, i) => (
                          <Badge key={i} variant="secondary" className="font-mono text-xs">
                            {t.trim()}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span data-testid={`text-inclusive-bot-${ev.id}`}>{ev.chatbotName}</span>
                        {" · "}
                        {fmt(ev.createdAt)}
                        {ev.remainingTerms ? (
                          <span className="text-amber-600 dark:text-amber-500">
                            {" · still present: "}
                            {ev.remainingTerms}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    {ev.rewritten ? (
                      <Badge
                        variant="outline"
                        className="shrink-0 border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                        data-testid={`badge-inclusive-rewritten-${ev.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Rewritten
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="shrink-0 border-amber-500/40 text-amber-700 dark:text-amber-400"
                        data-testid={`badge-inclusive-flagged-${ev.id}`}
                      >
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Flagged
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function VoiceInclusivityTab() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<VoiceProfile>({ queryKey: ["/api/organization/voice-profile"] });
  const [form, setForm] = useState<VoiceProfile | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const set = (key: keyof VoiceProfile, value: any) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form) return;
      await apiRequest("PUT", "/api/organization/voice-profile", {
        enabled: form.enabled,
        tone: form.tone,
        preferredTerms: form.preferredTerms,
        prohibitedTerms: form.prohibitedTerms,
        readingLevel: form.readingLevel,
        inclusivityStandard: form.inclusivityStandard,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/voice-profile"] });
      toast({ title: "Voice profile saved", description: "These standards now shape every chatbot response in your organization." });
    },
    onError: (err: Error) => {
      toast({ title: "Could not save", description: brandingFriendlyError(err), variant: "destructive" });
    },
  });

  if (isLoading || !form) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="text-voice-loading">
          Loading voice &amp; inclusivity profile…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Type className="w-5 h-5" />
            Institutional Voice &amp; Inclusivity
          </CardTitle>
          <CardDescription>
            Define the tone, terminology, and inclusive-language standards every chatbot must follow.
            These are injected into each response, and a post-generation guard scrubs any prohibited terms.
            Leave a field blank to use the built-in institutional default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="voice-enabled">Apply institutional voice profile</Label>
              <p className="text-xs text-muted-foreground">
                When off, only the baseline inclusivity safeguards remain in force.
              </p>
            </div>
            <Switch
              id="voice-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => set("enabled", v)}
              data-testid="switch-voice-enabled"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-tone">Voice &amp; tone</Label>
            <Textarea
              id="voice-tone"
              rows={3}
              value={form.tone}
              onChange={(e) => set("tone", e.target.value)}
              data-testid="input-voice-tone"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-preferred">Preferred terminology</Label>
            <Textarea
              id="voice-preferred"
              rows={3}
              value={form.preferredTerms}
              onChange={(e) => set("preferredTerms", e.target.value)}
              data-testid="input-voice-preferred"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-prohibited">Prohibited / non-inclusive terms</Label>
            <Textarea
              id="voice-prohibited"
              rows={3}
              value={form.prohibitedTerms}
              onChange={(e) => set("prohibitedTerms", e.target.value)}
              data-testid="input-voice-prohibited"
            />
            <p className="text-xs text-muted-foreground">
              Comma- or newline-separated. Any of these appearing in a response triggers an automatic inclusive rewrite.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-reading">Reading level</Label>
            <Textarea
              id="voice-reading"
              rows={2}
              value={form.readingLevel}
              onChange={(e) => set("readingLevel", e.target.value)}
              data-testid="input-voice-reading"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-inclusivity">Inclusive-language standard</Label>
            <Textarea
              id="voice-inclusivity"
              rows={3}
              value={form.inclusivityStandard}
              onChange={(e) => set("inclusivityStandard", e.target.value)}
              data-testid="input-voice-inclusivity"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end items-center gap-3">
        <InlineFormError error={saveMutation.isError ? saveMutation.error : null} data-testid="error-save-voice" />
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-voice">
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? "Saving..." : "Save Voice Profile"}
        </Button>
      </div>

      <InclusiveEventsCard />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: SmtpSettingsTab
// Lets an Org Admin configure a per-tenant outbound email provider
// (SMTP or SendGrid). Credentials are encrypted at rest server-side.
// ────────────────────────────────────────────────────────────────────────────
function SmtpSettingsTab() {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    enabled: false, provider: "smtp", host: "", port: 587,
    user: "", password: "", useSsl: true,
    fromEmail: "", fromName: "", sendGridApiKey: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["/api/organization/smtp"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organization/smtp");
      if (!res.ok) throw new Error("Failed to load SMTP settings");
      return res.json();
    },
  });

  useEffect(() => {
    if (data) {
      setForm({
        enabled: data.enabled ?? false,
        provider: data.provider ?? "smtp",
        host: data.host ?? "",
        port: data.port ?? 587,
        user: data.user ?? "",
        password: "",           // never pre-fill the masked password
        useSsl: data.useSsl ?? true,
        fromEmail: data.fromEmail ?? "",
        fromName: data.fromName ?? "",
        sendGridApiKey: "",     // never pre-fill the masked key
      });
    }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiRequest("PUT", "/api/organization/smtp", form);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Save failed");
      }
      queryClient.invalidateQueries({ queryKey: ["/api/organization/smtp"] });
      toast({ title: "Email settings saved" });
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await apiRequest("POST", "/api/organization/smtp/test");
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Test failed");
      toast({ title: "Test email sent", description: body.message });
    } catch (err: any) {
      toast({ title: "Test failed", description: err.message, variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Key className="w-5 h-5" />Email / SMTP
        </CardTitle>
        <CardDescription>
          Configure a custom outbound email provider for this workspace. When enabled, all emails from
          this workspace (password resets, notifications, approvals) use these settings instead of the
          platform defaults.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Enabled toggle ─────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <Switch
            checked={form.enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            id="smtp-enabled"
          />
          <Label htmlFor="smtp-enabled">Use custom email provider</Label>
        </div>

        {form.enabled && (
          <>
            {/* ── Provider ───────────────────────────────────── */}
            <div className="space-y-1.5">
              <Label htmlFor="smtp-provider">Provider</Label>
              <Select
                value={form.provider}
                onValueChange={(v) => setForm((f) => ({ ...f, provider: v }))}
              >
                <SelectTrigger id="smtp-provider" className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="smtp">SMTP</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── Provider-specific fields ────────────────────── */}
            {form.provider === "sendgrid" ? (
              <div className="space-y-1.5">
                <Label htmlFor="smtp-sg-key">SendGrid API Key</Label>
                <Input
                  id="smtp-sg-key"
                  type="password"
                  value={form.sendGridApiKey}
                  onChange={(e) => setForm((f) => ({ ...f, sendGridApiKey: e.target.value }))}
                  placeholder={data?.sendGridApiKey ? "Leave blank to keep existing key" : "SG.xxxxx…"}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-host">SMTP Host</Label>
                  <Input
                    id="smtp-host"
                    value={form.host}
                    onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                    placeholder="smtp.example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-port">Port</Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    value={form.port}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, port: parseInt(e.target.value) || 587 }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-user">Username</Label>
                  <Input
                    id="smtp-user"
                    value={form.user}
                    onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="smtp-password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      placeholder={data?.password ? "Leave blank to keep existing" : "Password"}
                      className="pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:col-span-2">
                  <Switch
                    checked={form.useSsl}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, useSsl: v }))}
                    id="smtp-ssl"
                  />
                  <Label htmlFor="smtp-ssl">Use SSL / TLS</Label>
                </div>
              </div>
            )}

            {/* ── Sender identity (shared) ────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="smtp-from-email">From Email</Label>
                <Input
                  id="smtp-from-email"
                  type="email"
                  value={form.fromEmail}
                  onChange={(e) => setForm((f) => ({ ...f, fromEmail: e.target.value }))}
                  placeholder="noreply@yourcompany.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="smtp-from-name">From Name</Label>
                <Input
                  id="smtp-from-name"
                  value={form.fromName}
                  onChange={(e) => setForm((f) => ({ ...f, fromName: e.target.value }))}
                  placeholder="Acme Inc."
                />
              </div>
            </div>
          </>
        )}

        {/* ── Actions ────────────────────────────────────────── */}
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={save} disabled={saving} data-testid="button-save-smtp">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save settings"}
          </Button>
          {/* Only show test button when SMTP is saved and enabled so there's
              something to actually test. */}
          {data?.enabled && form.enabled && (
            <Button variant="outline" onClick={test} disabled={testing} data-testid="button-test-smtp">
              {testing ? "Sending…" : "Send test email"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const branding = useBranding();
  const { toast } = useToast();
  const { user, organization } = useAuth();
  const { theme: currentMode } = useTheme();
  const isSuperAdmin = user?.role === "super_admin";
  const brandingIncluded = useFeature("custom_branding");
  const [orgName, setOrgName] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [tagline, setTagline] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const [logoTouched, setLogoTouched] = useState(false);
  const [logoError, setLogoError] = useState("");

  const [selectedPlatformTheme, setSelectedPlatformTheme] = useState(() => {
    return localStorage.getItem("platform-theme") || "app-maisters";
  });
  const [customColors, setCustomColors] = useState<ThemeColors | null>(null);
  const [isCustomMode, setIsCustomMode] = useState(false);

  useEffect(() => {
    const modeKey = `platform-custom-colors-${currentMode}`;
    const savedCustom = localStorage.getItem(modeKey);
    const savedFlag = localStorage.getItem(`platform-custom-mode-${currentMode}`);
    if (savedCustom && savedFlag === "true") {
      try {
        setCustomColors(JSON.parse(savedCustom));
        setIsCustomMode(true);
      } catch {
        setCustomColors(null);
        setIsCustomMode(false);
      }
    } else {
      setCustomColors(null);
      setIsCustomMode(false);
    }
  }, [currentMode]);

  useEffect(() => {
    if (isCustomMode && customColors) {
      applyPlatformTheme(customColors, currentMode);
    } else {
      const themeData = getPlatformThemeById(selectedPlatformTheme);
      const colors = currentMode === "dark" ? themeData.dark : themeData.light;
      applyPlatformTheme(colors, currentMode);
    }
  }, [selectedPlatformTheme, currentMode, isCustomMode, customColors]);

  const handleSelectPreset = (themeId: string) => {
    setSelectedPlatformTheme(themeId);
    setIsCustomMode(false);
    setCustomColors(null);
    localStorage.setItem("platform-theme", themeId);
    localStorage.removeItem(`platform-custom-colors-${currentMode}`);
    localStorage.setItem(`platform-custom-mode-${currentMode}`, "false");
    const themeData = getPlatformThemeById(themeId);
    const colors = currentMode === "dark" ? themeData.dark : themeData.light;
    applyPlatformTheme(colors, currentMode);
    toast({ title: "Theme applied", description: `Switched to ${themeData.name}` });
  };

  const handleStartCustomizing = () => {
    const themeData = getPlatformThemeById(selectedPlatformTheme);
    const base = currentMode === "dark" ? { ...themeData.dark } : { ...themeData.light };
    setCustomColors(base);
    setIsCustomMode(true);
    localStorage.setItem(`platform-custom-mode-${currentMode}`, "true");
    localStorage.setItem(`platform-custom-colors-${currentMode}`, JSON.stringify(base));
  };

  const handleCustomColorChange = (key: keyof ThemeColors, value: string) => {
    if (!customColors) return;
    const updated = { ...customColors, [key]: value };
    setCustomColors(updated);
    localStorage.setItem(`platform-custom-colors-${currentMode}`, JSON.stringify(updated));
    applyPlatformTheme(updated, currentMode);
  };

  const handleResetToPreset = () => {
    setIsCustomMode(false);
    setCustomColors(null);
    localStorage.removeItem(`platform-custom-colors-${currentMode}`);
    localStorage.setItem(`platform-custom-mode-${currentMode}`, "false");
    const themeData = getPlatformThemeById(selectedPlatformTheme);
    const colors = currentMode === "dark" ? themeData.dark : themeData.light;
    applyPlatformTheme(colors, currentMode);
    toast({ title: "Reset to preset", description: `Reverted to ${themeData.name}` });
  };

  const { data: plans } = useQuery<any[]>({ queryKey: ["/api/subscription-plans"] });

  // ── Billing summary (Stripe live data) ──────────────────────────────────
  const { data: billingSummary, isLoading: summaryLoading } = useQuery<any>({
    queryKey: ["/api/billing/summary"],
    retry: false,
    staleTime: 60_000,
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/portal");
      if (!res.ok) throw new Error((await res.json()).message ?? "Failed to open billing portal");
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    },
    onError: (err: any) => {
      toast({ title: "Could not open billing portal", description: err.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    setLogo((organization as any)?.logo ?? null);
    setTagline((organization as any)?.tagline ?? "");
    setLogoTouched(false);
  }, [(organization as any)?.logo, (organization as any)?.tagline]);

  const handleLogoFile = (file: File | null) => {
    setLogoError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoError("Please choose an image file.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setLogoError("Image is too large. Please use one under 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogo(reader.result as string);
      setLogoTouched(true);
    };
    reader.onerror = () => setLogoError("Could not read that file. Try another image.");
    reader.readAsDataURL(file);
  };

  const updateOrgMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", "/api/organization", {
        name: orgName || undefined,
        ...(brandingIncluded
          ? {
              brandColor: brandColor || undefined,
              tagline: tagline.trim(),
              ...(logoTouched ? { logo: logo ?? "" } : {}),
            }
          : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      setLogoTouched(false);
      toast({ title: "Organization updated", description: "Your branding is now live for everyone in this organization." });
    },
    onError: (err: Error) => {
      toast({ title: "Could not save", description: err.message, variant: "destructive" });
    },
  });

  const upgradeMutation = useMutation({
    mutationFn: async (planSlug: string) => {
      const res = await apiRequest("POST", "/api/organization/plan", { planSlug });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/entitlements"] });
      setDowngradeInfo(null);
      toast({ title: "Plan changed", description: data.message });
    },
    onError: (err: Error) => {
      toast({ title: "Plan change blocked", description: err.message, variant: "destructive" });
    },
  });

  // Downgrade pre-check: before switching plans, ask the backend whether
  // current usage fits the target plan. Blockers keep the confirm button
  // disabled; features lost are shown for informed consent.
  const [downgradeInfo, setDowngradeInfo] = useState<any | null>(null);
  const [checkingPlan, setCheckingPlan] = useState<string | null>(null);

  const handlePlanSelect = async (plan: any) => {
    setCheckingPlan(plan.slug);
    try {
      const res = await apiRequest("GET", `/api/organization/downgrade-check?planSlug=${encodeURIComponent(plan.slug)}`);
      const check = await res.json();
      if (check.canDowngrade && (!check.featuresLost || check.featuresLost.length === 0)) {
        upgradeMutation.mutate(plan.slug);
      } else {
        setDowngradeInfo({ ...check, planName: plan.name, planSlug: plan.slug });
      }
    } catch (err: any) {
      toast({ title: "Could not verify plan change", description: err.message, variant: "destructive" });
    } finally {
      setCheckingPlan(null);
    }
  };

  const activeColors = isCustomMode && customColors
    ? customColors
    : (currentMode === "dark"
      ? getPlatformThemeById(selectedPlatformTheme).dark
      : getPlatformThemeById(selectedPlatformTheme).light);

  return (
    <PageShell
      className="max-w-5xl"
      eyebrow="Workspace"
      title={<span data-testid="text-settings-title">Settings</span>}
      subtitle={`Configure your ${branding.productName} — landing page, theme, and subscription. AI provider keys live on each chatbot's Integrations tab.`}
      stats={
        <>
          <PageStat
            label="Organization"
            value={organization?.name ?? "\u2014"}
            hint={organization?.mode === "internal" ? "Internal" : (organization?.plan || "Free")}
            icon={<Building2 className="w-4 h-4" />}
          />
          <PageStat
            label="Theme"
            value={getPlatformThemeById(selectedPlatformTheme).name}
            hint={isCustomMode ? "Custom palette" : "Platform preset"}
            icon={<Palette className="w-4 h-4" />}
          />
          <PageStat
            label="Mode"
            value={currentMode === "dark" ? "Dark" : "Light"}
            hint="Auto-saved"
            icon={<SettingsIcon className="w-4 h-4" />}
          />
        </>
      }
    >
      <Tabs defaultValue="general" className="space-y-6">
        {/* 320px reflow: tabs become a full-width wrapping grid below sm */}
        <TabsList
          data-testid="tabs-settings"
          className="grid h-auto w-full max-w-full grid-cols-1 gap-1 min-[360px]:grid-cols-2 sm:inline-flex sm:w-auto"
        >
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Key className="w-3.5 h-3.5 mr-1.5" />Email
          </TabsTrigger>
          <TabsTrigger value="landing" data-testid="tab-landing">
            <Layout className="w-3.5 h-3.5 mr-1.5" />Landing Page
          </TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme">
            <Palette className="w-3.5 h-3.5 mr-1.5" />Platform Theme
          </TabsTrigger>
          <TabsTrigger value="voice" data-testid="tab-voice">
            <Type className="w-3.5 h-3.5 mr-1.5" />Voice &amp; Inclusivity
          </TabsTrigger>
          {organization?.mode !== "internal" && (
            <TabsTrigger value="plan" data-testid="tab-plan">Subscription</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5" />Organization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name</Label>
                  <Input id="org-name" placeholder={organization?.name || "Organization"} value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="input-org-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand-color">Brand Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" disabled={!brandingIncluded} value={brandColor || organization?.brandColor || "#0c3547"} onChange={(e) => setBrandColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50" aria-label="Brand colour picker" />
                    <Input id="brand-color" disabled={!brandingIncluded} placeholder={organization?.brandColor || "#0c3547"} value={brandColor} onChange={(e) => setBrandColor(e.target.value)} data-testid="input-brand-color" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-tagline">Tagline</Label>
                <Input id="org-tagline" disabled={!brandingIncluded} placeholder={branding.tagline} value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={120} data-testid="input-org-tagline" />
                <p className="text-xs text-muted-foreground">Shown under your brand name in the sidebar. Leave empty to use the default.</p>
                {!brandingIncluded && (
                  <p className="text-xs text-amber-600 dark:text-amber-500" data-testid="text-branding-locked">
                    {BRANDING_UPGRADE_MESSAGE}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Logo</Label>
                {/* 320px reflow: stack preview above upload controls */}
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="w-40 h-16 rounded-md border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
                    {logo ? (
                      <img src={logo} alt="Organization logo" className="max-h-14 max-w-[148px] object-contain" data-testid="img-logo-preview" />
                    ) : (
                      <span className="text-[11px] text-muted-foreground flex flex-col items-center gap-1">
                        <Image className="w-4 h-4" />No logo
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button asChild variant="outline" size="sm" disabled={!brandingIncluded} data-testid="button-upload-logo">
                        <label className={brandingIncluded ? "cursor-pointer" : "cursor-not-allowed opacity-50 pointer-events-none"}>
                          <Image className="w-4 h-4 mr-2" />Upload
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={!brandingIncluded}
                            onChange={(e) => handleLogoFile(e.target.files?.[0] ?? null)}
                            data-testid="input-logo-file"
                          />
                        </label>
                      </Button>
                      {logo && (
                        <Button variant="ghost" size="sm" disabled={!brandingIncluded} onClick={() => { setLogo(null); setLogoTouched(true); setLogoError(""); }} data-testid="button-remove-logo">
                          <Trash2 className="w-4 h-4 mr-2" />Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">PNG, JPG or SVG up to 1 MB. Replaces the text brand name in the sidebar.</p>
                    {logoError && <p className="text-xs text-destructive" data-testid="text-logo-error">{logoError}</p>}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted/50">
                <span className="text-sm">Landing Page</span>
                <a href={`/org/${organization?.slug}`} target="_blank" className="text-sm text-primary underline break-all min-w-0" data-testid="link-landing-page">/org/{organization?.slug}</a>
              </div>
              <InlineFormError error={updateOrgMutation.isError ? updateOrgMutation.error : null} data-testid="error-save-org" />
              <Button onClick={() => updateOrgMutation.mutate()} disabled={(brandingIncluded ? (!orgName && !brandColor && !logoTouched && (tagline.trim() === ((organization as any)?.tagline ?? ""))) : !orgName) || updateOrgMutation.isPending} data-testid="button-save-org">
                <Save className="w-4 h-4 mr-2" />
                {updateOrgMutation.isPending ? "Saving..." : "Save Organization"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Platform Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* 320px reflow: rows wrap, long values break inside the card */}
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted/50">
                  <span className="text-sm">Version</span>
                  <Badge variant="outline" className="max-w-full whitespace-normal break-words">2.0.0</Badge>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted/50">
                  <span className="text-sm">Platform</span>
                  <Badge variant="outline" className="max-w-full whitespace-normal break-words">{branding.productName}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="landing" className="space-y-6">
          {brandingIncluded ? <LandingPageTab organization={organization} /> : <BrandingUpgradeNotice />}
        </TabsContent>

        <TabsContent value="theme" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Color Palettes
              </CardTitle>
              <CardDescription>Choose a predefined color palette for the platform dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 min-[360px]:grid-cols-2 md:grid-cols-4 gap-3">
                {PLATFORM_THEMES.map((pt) => {
                  const isSelected = selectedPlatformTheme === pt.id && !isCustomMode;
                  const colors = currentMode === "dark" ? pt.dark : pt.light;
                  return (
                    <button
                      key={pt.id}
                      onClick={() => handleSelectPreset(pt.id)}
                      className={`relative p-3 rounded-lg border-2 text-left transition-all hover:scale-[1.02] ${isSelected ? "border-accent ring-2 ring-accent/20" : "border-border hover:border-accent/50"}`}
                      data-testid={`theme-preset-${pt.id}`}
                    >
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                          <Check className="w-3 h-3 text-accent-foreground" />
                        </div>
                      )}
                      <div className="flex gap-1 mb-2">
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: `hsl(${colors.sidebar})` }} />
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: `hsl(${colors.primary})` }} />
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: `hsl(${colors.accent})` }} />
                        <div className="w-6 h-6 rounded border border-border" style={{ backgroundColor: `hsl(${colors.background})` }} />
                      </div>
                      <p className="text-xs font-semibold">{pt.name}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{pt.description}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Custom Colors
                  </CardTitle>
                  <CardDescription>Fine-tune individual colors for {currentMode === "dark" ? "dark" : "light"} mode</CardDescription>
                </div>
                <div className="flex gap-2">
                  {isCustomMode && (
                    <Button variant="outline" size="sm" onClick={handleResetToPreset} data-testid="button-reset-theme">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Reset
                    </Button>
                  )}
                  {!isCustomMode && (
                    <Button variant="outline" size="sm" onClick={handleStartCustomizing} data-testid="button-customize-colors">
                      <Palette className="w-3.5 h-3.5 mr-1" />Customize
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isCustomMode && customColors ? (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Sidebar</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <ColorPicker label="Background" value={customColors.sidebar} onChange={(v) => handleCustomColorChange("sidebar", v)} testId="color-sidebar" />
                      <ColorPicker label="Text" value={customColors.sidebarForeground} onChange={(v) => handleCustomColorChange("sidebarForeground", v)} testId="color-sidebar-text" />
                      <ColorPicker label="Active Item" value={customColors.sidebarPrimary} onChange={(v) => handleCustomColorChange("sidebarPrimary", v)} testId="color-sidebar-primary" />
                      <ColorPicker label="Hover" value={customColors.sidebarAccent} onChange={(v) => handleCustomColorChange("sidebarAccent", v)} testId="color-sidebar-accent" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Main Content</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <ColorPicker label="Background" value={customColors.background} onChange={(v) => handleCustomColorChange("background", v)} testId="color-background" />
                      <ColorPicker label="Text" value={customColors.foreground} onChange={(v) => handleCustomColorChange("foreground", v)} testId="color-foreground" />
                      <ColorPicker label="Card" value={customColors.card} onChange={(v) => handleCustomColorChange("card", v)} testId="color-card" />
                      <ColorPicker label="Border" value={customColors.border} onChange={(v) => handleCustomColorChange("border", v)} testId="color-border" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Brand Colors</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <ColorPicker label="Primary" value={customColors.primary} onChange={(v) => handleCustomColorChange("primary", v)} testId="color-primary" />
                      <ColorPicker label="Primary Text" value={customColors.primaryForeground} onChange={(v) => handleCustomColorChange("primaryForeground", v)} testId="color-primary-fg" />
                      <ColorPicker label="Accent" value={customColors.accent} onChange={(v) => handleCustomColorChange("accent", v)} testId="color-accent" />
                      <ColorPicker label="Accent Text" value={customColors.accentForeground} onChange={(v) => handleCustomColorChange("accentForeground", v)} testId="color-accent-fg" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Secondary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <ColorPicker label="Secondary" value={customColors.secondary} onChange={(v) => handleCustomColorChange("secondary", v)} testId="color-secondary" />
                      <ColorPicker label="Secondary Text" value={customColors.secondaryForeground} onChange={(v) => handleCustomColorChange("secondaryForeground", v)} testId="color-secondary-fg" />
                      <ColorPicker label="Muted" value={customColors.muted} onChange={(v) => handleCustomColorChange("muted", v)} testId="color-muted" />
                      <ColorPicker label="Muted Text" value={customColors.mutedForeground} onChange={(v) => handleCustomColorChange("mutedForeground", v)} testId="color-muted-fg" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Palette className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">Want to fine-tune individual colors?</p>
                  <p className="text-xs text-muted-foreground mb-4">Click "Customize" above to start adjusting colors for {currentMode} mode.</p>
                  <Button variant="outline" size="sm" onClick={handleStartCustomizing} data-testid="button-customize-colors-cta">
                    <Palette className="w-3.5 h-3.5 mr-1" />Start Customizing
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Live Preview</CardTitle>
              <CardDescription>See how your current theme looks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <div className="flex">
                  <div className="w-48 p-3 space-y-2" style={{ backgroundColor: `hsl(${activeColors.sidebar})`, color: `hsl(${activeColors.sidebarForeground})` }}>
                    <p className="text-xs font-bold">Sidebar</p>
                    <div className="rounded px-2 py-1.5 text-xs" style={{ backgroundColor: `hsl(${activeColors.sidebarPrimary})`, color: "white" }}>Active Item</div>
                    <div className="rounded px-2 py-1.5 text-xs" style={{ backgroundColor: `hsl(${activeColors.sidebarAccent})` }}>Hover Item</div>
                    <div className="px-2 py-1.5 text-xs opacity-70">Regular Item</div>
                  </div>
                  <div className="flex-1 p-4 space-y-3" style={{ backgroundColor: `hsl(${activeColors.background})`, color: `hsl(${activeColors.foreground})` }}>
                    <p className="text-sm font-bold">Main Content</p>
                    <div className="rounded-md border p-3 space-y-2" style={{ backgroundColor: `hsl(${activeColors.card})`, borderColor: `hsl(${activeColors.border})` }}>
                      <p className="text-xs font-medium" style={{ color: `hsl(${activeColors.cardForeground})` }}>Card Component</p>
                      <p className="text-[10px]" style={{ color: `hsl(${activeColors.mutedForeground})` }}>Muted description text</p>
                      <div className="flex gap-2">
                        <div className="rounded px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: `hsl(${activeColors.primary})`, color: `hsl(${activeColors.primaryForeground})` }}>Primary</div>
                        <div className="rounded px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: `hsl(${activeColors.accent})`, color: `hsl(${activeColors.accentForeground})` }}>Accent</div>
                        <div className="rounded px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: `hsl(${activeColors.secondary})`, color: `hsl(${activeColors.secondaryForeground})` }}>Secondary</div>
                        <div className="rounded px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: `hsl(${activeColors.muted})`, color: `hsl(${activeColors.mutedForeground})` }}>Muted</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="voice" className="space-y-6">
          <VoiceInclusivityTab />
        </TabsContent>

        <TabsContent value="plan" className="space-y-6">
          {isSuperAdmin ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5" />Super Admin Access</CardTitle>
                <CardDescription>
                  You have unlimited access as a platform super admin. No subscription limits apply.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Super admins are not bound by subscription plans. You have unlimited access to agents, bots, and messages.
                    Client organizations are managed from the Super Admin panel.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
            {/* ── Billing Summary card ───────────────────────────────── */}
            {billingSummary && !billingSummary.localOnly && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-muted-foreground" />
                      Billing Summary
                    </CardTitle>
                    {billingSummary.hasStripeCustomer && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => portalMutation.mutate()}
                        disabled={portalMutation.isPending}
                        data-testid="button-billing-portal"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        {portalMutation.isPending ? "Opening…" : "Manage Billing"}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    {/* Status */}
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Status</p>
                      <Badge
                        variant={billingSummary.status === "active" ? "default" : "destructive"}
                        className="capitalize text-xs"
                      >
                        {billingSummary.status}
                      </Badge>
                    </div>
                    {/* Payment method */}
                    {billingSummary.paymentMethod && (
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Payment Method</p>
                        <p className="font-medium capitalize">
                          {billingSummary.paymentMethod.brand} ···· {billingSummary.paymentMethod.last4}
                          <span className="text-xs text-muted-foreground ml-1">
                            exp {billingSummary.paymentMethod.expiry}
                          </span>
                        </p>
                      </div>
                    )}
                    {/* Current plan */}
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Billed Plan</p>
                      <p className="font-medium capitalize">{billingSummary.plan}</p>
                    </div>
                    {/* Next billing date */}
                    {billingSummary.currentPeriodEnd && (
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />Next Billing
                        </p>
                        <p className="font-medium">
                          {new Date(billingSummary.currentPeriodEnd).toLocaleDateString(undefined, {
                            year: "numeric", month: "short", day: "numeric",
                          })}
                          {billingSummary.cancelAtPeriodEnd && (
                            <span className="ml-1 text-xs text-destructive">(cancels)</span>
                          )}
                        </p>
                      </div>
                    )}
                    {/* Next invoice amount */}
                    {billingSummary.nextInvoiceAmountCents != null && (
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                          <Receipt className="w-3 h-3" />Next Invoice
                        </p>
                        <p className="font-medium">
                          {(billingSummary.nextInvoiceAmountCents / 100).toLocaleString(undefined, {
                            style: "currency", currency: billingSummary.currency ?? "USD",
                          })}
                        </p>
                      </div>
                    )}
                    {/* Last payment */}
                    {billingSummary.latestInvoice && (
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Last Payment</p>
                        <p className="font-medium flex items-center gap-1">
                          {billingSummary.latestInvoice.paid
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                            : <AlertCircle className="w-3.5 h-3.5 text-destructive" />}
                          {billingSummary.latestInvoice.paid ? "Paid" : billingSummary.latestInvoice.status}
                          {billingSummary.latestInvoice.amountPaid > 0 && (
                            <span className="text-xs text-muted-foreground ml-0.5">
                              {(billingSummary.latestInvoice.amountPaid / 100).toLocaleString(undefined, {
                                style: "currency", currency: billingSummary.latestInvoice.currency ?? "USD",
                              })}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Local-only notice when no Stripe subscription exists */}
            {billingSummary?.localOnly && !summaryLoading && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                {billingSummary?.hasStripeCustomer ? (
                  /* Stripe customer exists but subscription is inactive/lapsed — portal available */
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span>A Stripe account exists but no active subscription is running for this organization.</span>
                    <Button
                      size="sm" variant="outline"
                      className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
                      onClick={() => portalMutation.mutate()}
                      disabled={portalMutation.isPending}
                      data-testid="button-billing-portal-local"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      {portalMutation.isPending ? "Opening…" : "Manage Billing"}
                    </Button>
                  </div>
                ) : (
                  /* No Stripe customer — needs to subscribe from scratch */
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span>No Stripe subscription set up — set up billing to activate your plan.</span>
                    <Button
                      size="sm" variant="outline"
                      className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
                      onClick={() => { window.location.href = "/billing/select-plan"; }}
                      data-testid="button-billing-setup"
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                      Set Up Billing
                    </Button>
                  </div>
                )}
              </div>
            )}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5" />Subscription Plan</CardTitle>
                <CardDescription>
                  Current plan: <Badge variant="default">{organization?.plan}</Badge>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {plans?.map((plan: any) => {
                    const isCurrent = organization?.plan === plan.slug;
                    return (
                      <Card key={plan.id} className={`flex flex-col ${isCurrent ? "ring-2 ring-primary" : ""}`}>
                        <CardContent className="p-4 space-y-3 flex-1 flex flex-col">
                          <div>
                            <p className="font-bold text-lg" data-testid={`text-plan-name-${plan.slug}`}>{plan.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Up to {plan.botLimit ?? 0} bots
                            </p>
                            <p className="text-2xl font-bold">${plan.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                          </div>
                          <ul className="space-y-1 h-48 shrink-0 overflow-y-auto overflow-x-hidden pr-1" data-testid={`features-plan-${plan.slug}`}>
                            {planDisplayFeatures(plan).map((f: string, i: number) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />{f}
                              </li>
                            ))}
                          </ul>
                          {isCurrent ? (
                            <Badge variant="outline" className="w-full justify-center">Current Plan</Badge>
                          ) : (
                            <Button variant="outline" size="sm" className="w-full" onClick={() => handlePlanSelect(plan)} disabled={upgradeMutation.isPending || checkingPlan !== null} data-testid={`button-upgrade-${plan.slug}`}>
                              {checkingPlan === plan.slug ? "Checking…" : plan.price > (plans?.find((p: any) => p.slug === organization?.plan)?.price || 0) ? "Upgrade" : "Switch"}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <InlineFormError error={upgradeMutation.isError ? upgradeMutation.error : null} className="mt-3" data-testid="error-plan-change" />
              </CardContent>
            </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="email" className="space-y-6">
          <SmtpSettingsTab />
        </TabsContent>
      </Tabs>

      <Dialog open={!!downgradeInfo} onOpenChange={(open) => { if (!open) setDowngradeInfo(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {downgradeInfo?.canDowngrade ? `Switch to ${downgradeInfo?.planName}?` : "Downgrade requirements not met"}
            </DialogTitle>
            <DialogDescription>
              {downgradeInfo?.canDowngrade
                ? `Review what changes before switching from ${downgradeInfo?.currentPlan} to ${downgradeInfo?.planSlug}.`
                : `Your current usage exceeds what the ${downgradeInfo?.planName} plan allows. Resolve the items below, then try again.`}
            </DialogDescription>
          </DialogHeader>

          {downgradeInfo?.blockers?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-destructive">Must be resolved before switching:</p>
              <ul className="space-y-2">
                {downgradeInfo.blockers.map((b: any, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`text-downgrade-blocker-${b.type}`}>
                    <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                    <span>{b.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {downgradeInfo?.featuresLost?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold">Features you will lose:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                {downgradeInfo.featuresLost.map((f: string, i: number) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                    <Trash2 className="w-3 h-3 text-destructive flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDowngradeInfo(null)} data-testid="button-downgrade-cancel">
              {downgradeInfo?.canDowngrade ? "Cancel" : "Close"}
            </Button>
            <Button
              variant="destructive"
              disabled={!downgradeInfo?.canDowngrade || upgradeMutation.isPending}
              onClick={() => upgradeMutation.mutate(downgradeInfo.planSlug)}
              data-testid="button-downgrade-confirm"
            >
              {downgradeInfo?.canDowngrade ? `Confirm switch to ${downgradeInfo?.planName}` : "Resolve downgrade requirements"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
